import { Bar } from "@ant-design/charts";
import { getAllWorkers } from '../../../services/reports.service';
import { ReactNode, useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import dayjs from "dayjs";
import { Empty, Space, Spin } from "antd";
import { formatCurrency, formatDatePt } from "../../../services/utils";
import ExportBtn from "../../../components/Commons/Export/ExportButton";
import { usePDF } from "@react-pdf/renderer";
import ChartPDF from "./ChartPDF";
import FilterButton from "../../../components/Commons/Table/FilterButton";
import { isMobile } from "react-device-detect";

interface IProps {
    type: string,
    workerId: number | (number | string)[],
    startDate?: string,
    endDate?: string,
    filters: ReactNode
}

interface IWorker {
    'Operador': string,
    'Data': string,
    'start': string,
    'Valor': number,
    'Quantidade de serviços': string
}

export default function GeneralPerDayChart({
    type, workerId, startDate, endDate, filters
}: IProps) {
    const title = 'Gráfico Geral - Por dia';

    const barRef = useRef<any>();
    const [workers, setWorkers] = useState<IWorker[]>([]);
    const [isExportingPDF, setIsExportingPDF] = useState<boolean>(false);
    const [pdfInstante, updatePdfInstante] = usePDF({
        document: <ChartPDF title={title} chartBase64Encode={''} />
    });

    const { isLoading } = useQuery(
        ['loadWorkers', startDate, endDate, workerId],
        () => startDate && endDate
            ? getAllWorkers({ type, start_date: startDate, end_date: endDate, worker_id: workerId, perDay: '1' })
            : null,
        {
            onSuccess: response => {
                if (response) {
                    let _workers: IWorker[] = response.data.reduce((array: [], worker: any) => {
                        return array.concat(worker.data.perDay.map((day: any) => {
                            return {
                                'Operador': worker.data.name,
                                'Data': formatDatePt(day.start),
                                'start': day.start,
                                'Valor': day.value,
                                'Quantidade de serviços': day.quantity
                            }
                        }));
                    }, []);

                    _workers.sort((a: any, b: any) => new Date(a.start).valueOf() - new Date(b.start).valueOf());

                    setWorkers(_workers);
                }
            },
            refetchOnWindowFocus: false
        }
    );

    const config = {
        data: workers,
        height: 500,
        xField: 'Data',
        yField: 'Valor',
        colorField: 'Operador',
        stack: true,
        tooltip: (
            datum: any,
            index: number,
            data: any,
            column: any,
        ) => {
            return (
                {
                    name: datum.Data,
                    field: column.y.field,
                    value: `
                        ${datum['Quantidade de serviços']}
                        Serviço${datum['Quantidade de serviços'] === 1 ? '' : 's'}:
                        ${formatCurrency(datum.Valor)}
                    `
                }
            );
        },
        children: [
            {
                type: 'interval',
                yField: 'Valor',
                axis: {
                    y: {
                        type: 'interval',
                        labelFormatter: (datum: any) => formatCurrency(datum || 0),
                        labelTransform: 'rotate(0)'
                    }
                }
            },
        ]
    };

    const exportChartToPDF = () => {
        updatePdfInstante(<ChartPDF
            title={title}
            chartBase64Encode={barRef.current?.toDataURL('image/png', 1)} />);

        setTimeout(() => {
            setIsExportingPDF(true);
        }, 1000);
    };

    useEffect(() => {
        if (isExportingPDF) {
            window.open(pdfInstante.url || '#', '_blank');

            setIsExportingPDF(false);
        }
    }, [isExportingPDF]);

    return (<>
        <div className="zone_result_size_n_export" style={isMobile ? { marginTop: 10 } : {}}>
            <div></div>
            <Space>
                <FilterButton filters={filters} />
                <ExportBtn
                    loading={pdfInstante.loading || isLoading}
                    disabled={!workers.length}
                    onClick={exportChartToPDF} />
            </Space>
        </div>

        {!workers.length && !isLoading ?
            <Empty
                image="/empty-table.svg"
                imageStyle={{ height: 85 }}
                description="Nenhum dado a ser exibido">
            </Empty> : ''
        }

        <Spin spinning={isLoading}>
            <Bar {...config} ref={barRef} />
        </Spin>
    </>)
}
